import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth, type SubscriptionStatus } from '../auth/AuthContext';
import { AccountSettings } from '../components/AccountSettings';
import { AdminLayout } from '../components/AdminLayout';
import { GenerateTestPanel } from './dashboard/GenerateTestPanel';
import { RecentResultsPanel } from './dashboard/RecentResultsPanel';
import { TestPanel } from './dashboard/TestPanel';
import { showErrorNotification, useErrorNotification } from '../hooks/useErrorNotification';
import {
  AttemptDetail,
  AttemptListEntry,
  ChoiceKey,
  CreateResponse,
  MathTopicId,
  MATH_TOPICS,
  ProgressPayload,
  ProgressState,
  ReviewEntry,
  StatusMessage,
  SubmitResponse,
  TestPayload,
  RESULTS_PAGE_SIZE,
} from './dashboard/types';

export function DashboardPage() {
  const { user, authedRequest, logout, loginSuccess, authToken } = useAuth();
  const [count, setCount] = useState<number>(5);
  const [selectedTopics, setSelectedTopics] = useState<Set<MathTopicId>>(
    () => new Set(MATH_TOPICS.map((topic) => topic.id))
  );
  const [difficulty, setDifficulty] = useState<number>(8);
  const [loading, setLoading] = useState<boolean>(false);
  const [test, setTest] = useState<TestPayload | null>(null);
  const [answers, setAnswers] = useState<Record<string, ChoiceKey | undefined>>({});
  const [status, setStatus] = useState<StatusMessage | null>(null);
  const [results, setResults] = useState<AttemptListEntry[]>([]);
  const [resultsTotal, setResultsTotal] = useState<number>(0);
  const [resultsPage, setResultsPage] = useState<number>(1);
  const [resultsLoading, setResultsLoading] = useState<boolean>(false);
  const [resultsPageSize, setResultsPageSize] = useState<number>(RESULTS_PAGE_SIZE);
  const [generation, setGeneration] = useState<ProgressState | null>(null);
  const [review, setReview] = useState<ReviewEntry[] | null>(null);
  const [attemptReviews, setAttemptReviews] = useState<Record<string, AttemptDetail | 'loading' | 'error'>>({});
  const [expandedAttemptIds, setExpandedAttemptIds] = useState<Set<string>>(new Set());
  const [activeView, setActiveView] = useState<'home' | 'account'>('home');
  const pollRef = useRef<number | null>(null);
  const activeTestRef = useRef<string | null>(null);
  const resultsFetchRef = useRef(0);

  const subscriptionStatus = user?.subscriptionStatus ?? null;
  const subscriptionBlocked = Boolean(
    user && user.role !== 'admin' && subscriptionStatus && !subscriptionStatus.allowed
  );

  useErrorNotification(status, { header: 'Practice workspace error' });

  useEffect(() => {
    if (subscriptionBlocked) {
      setActiveView('account');
      const summary =
        subscriptionStatus?.summary ?? 'A valid subscription is required to use this feature.';
      setStatus((prev) => {
        if (prev?.type === 'err' && prev.text === summary) {
          return prev;
        }
        return { type: 'err', text: summary };
      });
    }
  }, [subscriptionBlocked, subscriptionStatus?.summary]);

  const handleSubscriptionError = useCallback(
    (err: any) => {
      if (err?.status === 402 && err?.payload?.subscriptionStatus) {
        const nextStatus = err.payload.subscriptionStatus as SubscriptionStatus;
        if (user && authToken) {
          loginSuccess(authToken, { ...user, subscriptionStatus: nextStatus });
        }
        setActiveView('account');
        const message =
          typeof err?.payload?.summary === 'string' && err.payload.summary.trim().length > 0
            ? err.payload.summary
            : 'A valid subscription is required to continue.';
        setStatus({ type: 'err', text: message });
        return true;
      }
      return false;
    },
    [authToken, loginSuccess, user]
  );

  function stopProgressPolling() {
    if (pollRef.current !== null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }

  function logoutAndReset() {
    stopProgressPolling();
    activeTestRef.current = null;
    setTest(null);
    setGeneration(null);
    setReview(null);
    setResults([]);
    setResultsTotal(0);
    setResultsPage(1);
    setResultsLoading(false);
    setResultsPageSize(RESULTS_PAGE_SIZE);
    setAttemptReviews({});
    setExpandedAttemptIds(new Set());
    setAnswers({});
    setStatus(null);
    setLoading(false);
    setActiveView('home');
    resultsFetchRef.current = 0;
    logout();
  }

  async function loadProgress(testId: string) {
    if (activeTestRef.current !== testId) return;
    if (subscriptionBlocked) {
      stopProgressPolling();
      activeTestRef.current = null;
      return;
    }
    try {
      const data = await authedRequest<ProgressPayload>(`/api/tests/${testId}/progress`);
      if (activeTestRef.current !== testId) return;
      setGeneration({ testId, status: data.status, progress: data.progress, error: data.error });

      if (data.status === 'completed') {
        stopProgressPolling();
        activeTestRef.current = null;
        try {
          const testPayload = await authedRequest<TestPayload>(`/api/tests/${testId}`);
          setTest(testPayload);
          setStatus({ type: 'ok', text: 'Test ready. Good luck!' });
        } catch (e: any) {
          if (!handleSubscriptionError(e)) {
            setStatus({ type: 'err', text: e.message || 'Failed to fetch generated test.' });
          }
        } finally {
          setLoading(false);
          setGeneration(null);
        }
      } else if (data.status === 'failed') {
        stopProgressPolling();
        activeTestRef.current = null;
        setLoading(false);
        setGeneration(null);
        setStatus({ type: 'err', text: data.error || 'Generation failed. Please try again.' });
      }
    } catch (e: any) {
      stopProgressPolling();
      activeTestRef.current = null;
      setLoading(false);
      setGeneration(null);
      if (!handleSubscriptionError(e)) {
        setStatus({ type: 'err', text: e.message || 'Unable to check progress. Please try again.' });
      }
    }
  }

  function startProgressPolling(testId: string) {
    stopProgressPolling();
    pollRef.current = window.setInterval(() => {
      void loadProgress(testId);
    }, 1500);
  }

  async function generate() {
    stopProgressPolling();
    activeTestRef.current = null;
    setGeneration(null);
    if (subscriptionBlocked) {
      const summary =
        subscriptionStatus?.summary ?? 'A valid subscription is required to generate tests.';
      setStatus({ type: 'err', text: summary });
      setActiveView('account');
      return;
    }
    setLoading(true);
    setStatus(null);
    setTest(null);
    setAnswers({});
    setReview(null);
    resultsFetchRef.current += 1;
    try {
      const topics = Array.from(selectedTopics);
      if (topics.length === 0) {
        setLoading(false);
        setStatus({ type: 'err', text: 'Please select at least one topic.' });
        return;
      }
      const payload = await authedRequest<CreateResponse>('/api/tests', {
        method: 'POST',
        body: JSON.stringify({ count: Number(count), topics, difficulty }),
      });
      activeTestRef.current = payload.testId;
      setGeneration({ testId: payload.testId, status: payload.status, progress: payload.progress, error: null });
      setStatus({ type: 'info', text: 'Generating questions...' });
      await loadProgress(payload.testId);
      if (activeTestRef.current === payload.testId) {
        startProgressPolling(payload.testId);
      }
    } catch (e: any) {
      setLoading(false);
      if (!handleSubscriptionError(e)) {
        setStatus({ type: 'err', text: e.message || 'Failed to start generation.' });
      }
      setGeneration(null);
    }
  }

  function toggleTopic(topicId: MathTopicId) {
    setSelectedTopics((prev) => {
      const next = new Set(prev);
      if (next.has(topicId)) {
        next.delete(topicId);
      } else {
        next.add(topicId);
      }
      return next;
    });
  }

  function setAllTopics(selected: boolean) {
    setSelectedTopics(() => {
      if (!selected) {
        return new Set<MathTopicId>();
      }
      return new Set(MATH_TOPICS.map((topic) => topic.id));
    });
  }

  const handleCountChange = (value: number) => {
    setCount(value);
  };

  const handleDifficultyChange = (value: number) => {
    setDifficulty(value);
  };

  const handleSelectAnswer = useCallback((questionId: string, choice: ChoiceKey) => {
    setAnswers((prev) => ({ ...prev, [questionId]: choice }));
  }, []);

  async function submit() {
    if (!test) return;
    if (subscriptionBlocked) {
      const summary =
        subscriptionStatus?.summary ?? 'A valid subscription is required to submit results.';
      setStatus({ type: 'err', text: summary });
      setActiveView('account');
      return;
    }
    const responses = Object.entries(answers)
      .filter(([, v]) => !!v)
      .map(([qid, choice]) => ({ questionId: qid, choice }));
    if (responses.length === 0) {
      setStatus({ type: 'err', text: 'Please select at least one answer.' });
      return;
    }
    setStatus({ type: 'info', text: 'Submitting and marking...' });
    try {
      const data = await authedRequest<SubmitResponse>(`/api/tests/${test.testId}/submit`, {
        method: 'POST',
        body: JSON.stringify({ responses }),
      });
      setStatus({ type: 'ok', text: `Score: ${data.score_percent}%` });
      setReview(data.review || null);
      if (resultsPage === 1) {
        await refreshResults(1);
      } else {
        setResultsPage(1);
      }
    } catch (e: any) {
      if (!handleSubscriptionError(e)) {
        setStatus({ type: 'err', text: e.message || 'Unable to submit results.' });
      }
    }
  }

  const refreshResults = useCallback(
    async (page: number) => {
      const fetchId = ++resultsFetchRef.current;
      setResultsLoading(true);
      if (subscriptionBlocked) {
        if (resultsFetchRef.current === fetchId) {
          setResultsLoading(false);
        }
        return;
      }
      try {
        const params = new URLSearchParams({
          page: String(page),
          pageSize: String(RESULTS_PAGE_SIZE),
        });
        const data = await authedRequest<{
          attempts: AttemptListEntry[];
          total: number;
          page: number;
          pageSize: number;
        }>(`/api/results?${params.toString()}`);

        if (resultsFetchRef.current !== fetchId) {
          return;
        }

        const total = data.total ?? 0;
        const pageSize = data.pageSize ?? RESULTS_PAGE_SIZE;
        const lastPage = Math.max(1, Math.ceil(total / pageSize) || 1);

        setResultsPageSize(pageSize);
        setResultsTotal(total);

        if (page > lastPage) {
          setResultsPage(lastPage);
          return;
        }

        setResults(data.attempts || []);
        setExpandedAttemptIds((prev) => {
          if (prev.size === 0) return prev;
          const idsOnPage = new Set((data.attempts || []).map((attempt) => attempt.id));
          const next = new Set<string>();
          prev.forEach((id) => {
            if (idsOnPage.has(id)) {
              next.add(id);
            }
          });
          return next;
        });
      } catch (e: any) {
        if (resultsFetchRef.current !== fetchId) {
          return;
        }
        if (handleSubscriptionError(e)) {
          return;
        }
        const message =
          typeof e?.serverError === 'string' && e.serverError.trim().length > 0
            ? e.serverError
            : e?.message || 'Failed to load recent results.';
        showErrorNotification(message, { header: 'Results error' });
        // Preserve previous results when loading fails
      } finally {
        if (resultsFetchRef.current === fetchId) {
          setResultsLoading(false);
        }
      }
    },
    [authedRequest, handleSubscriptionError, subscriptionBlocked]
  );

  async function loadAttemptReview(attemptId: string) {
    setAttemptReviews((prev) => ({ ...prev, [attemptId]: 'loading' }));
    if (subscriptionBlocked) {
      setAttemptReviews((prev) => ({ ...prev, [attemptId]: 'error' }));
      return;
    }
    try {
      const data = await authedRequest<AttemptDetail>(`/api/results/attempts/${attemptId}`);
      setAttemptReviews((prev) => ({ ...prev, [attemptId]: data }));
    } catch (e: any) {
      setAttemptReviews((prev) => ({ ...prev, [attemptId]: 'error' }));
      if (!handleSubscriptionError(e)) {
        const message = e?.message || 'Unable to load review.';
        showErrorNotification(message, { header: 'Review error' });
      }
    }
  }

  function toggleAttempt(attemptId: string) {
    setExpandedAttemptIds((prev) => {
      const next = new Set(prev);
      if (next.has(attemptId)) {
        next.delete(attemptId);
      } else {
        next.add(attemptId);
        if (
          !subscriptionBlocked &&
          (!attemptReviews[attemptId] || attemptReviews[attemptId] === 'error')
        ) {
          void loadAttemptReview(attemptId);
        }
      }
      return next;
    });
  }

  useEffect(() => {
    if (subscriptionBlocked) {
      setResults([]);
      setResultsTotal(0);
      setExpandedAttemptIds(new Set());
      return;
    }
    void refreshResults(resultsPage);
  }, [refreshResults, resultsPage, subscriptionBlocked]);

  useEffect(() => {
    return () => {
      stopProgressPolling();
    };
  }, []);

  const progressPercent = generation && generation.progress.total > 0
    ? Math.min(100, Math.round((generation.progress.current / generation.progress.total) * 100))
    : 0;

  const topicLabelMap = useMemo(() => {
    return MATH_TOPICS.reduce<Record<string, string>>((acc, topic) => {
      acc[topic.id] = topic.label;
      return acc;
    }, {});
  }, []);

  const safePageSize = Math.max(resultsPageSize, 1);
  const totalPages = resultsTotal === 0 ? 1 : Math.max(1, Math.ceil(resultsTotal / safePageSize));
  const pageStart = resultsTotal === 0 ? 0 : (resultsPage - 1) * safePageSize + 1;
  const pageEnd = resultsTotal === 0 ? 0 : Math.min(resultsTotal, resultsPage * safePageSize);

  const headerActions = (
    <div className="btn-group btn-group-sm" role="group" aria-label="Practice workspace view selector">
      <button
        type="button"
        className={`btn ${activeView === 'home' ? 'btn-primary' : 'btn-outline-primary'}`}
        onClick={() => {
          if (!subscriptionBlocked) {
            setActiveView('home');
          }
        }}
        disabled={subscriptionBlocked}
      >
        Practice
      </button>
      <button
        type="button"
        className={`btn ${activeView === 'account' ? 'btn-primary' : 'btn-outline-primary'}`}
        onClick={() => setActiveView('account')}
      >
        Account
      </button>
    </div>
  );

  return (
    <AdminLayout
      title="Practice workspace"
      subtitle="Generate personalised selective school practice tests and monitor your progress."
      headerActions={headerActions}
      onLogout={logoutAndReset}
    >
      {activeView === 'account' ? (
        <div className="row g-3">
          <div className="col-12">
            <AccountSettings
              user={user}
              authedRequest={authedRequest}
              authToken={authToken}
              loginSuccess={loginSuccess}
            />
          </div>
        </div>
      ) : (
        <>
          <div className="row g-3 align-items-stretch">
            <div className="col-12 col-lg-4">
              <GenerateTestPanel
                count={count}
                onCountChange={handleCountChange}
                difficulty={difficulty}
                onDifficultyChange={handleDifficultyChange}
                selectedTopics={selectedTopics}
                onToggleTopic={toggleTopic}
                onSetAllTopics={setAllTopics}
                onGenerate={generate}
                loading={loading}
                generation={generation}
                progressPercent={progressPercent}
                status={status}
                hasTest={!!test}
              />
            </div>
            <div className="col-12 col-lg-8">
              {test ? (
                <TestPanel
                  test={test}
                  answers={answers}
                  onSelectAnswer={handleSelectAnswer}
                  onSubmit={submit}
                  status={status}
                  review={review}
                />
              ) : (
                <div className="card card-info card-outline h-100">
                  <div className="card-body text-center text-muted d-flex flex-column justify-content-center py-5">
                    <i className="fas fa-clipboard-list fa-3x mb-3 text-primary" aria-hidden="true" />
                    <p className="mb-0">Generate a test to see questions here.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="row g-3 mt-1">
            <div className="col-12">
              <RecentResultsPanel
                results={results}
                resultsLoading={resultsLoading}
                resultsTotal={resultsTotal}
                expandedAttemptIds={expandedAttemptIds}
                onToggleAttempt={toggleAttempt}
                attemptReviews={attemptReviews}
                topicLabelMap={topicLabelMap}
                pageStart={pageStart}
                pageEnd={pageEnd}
                resultsPage={resultsPage}
                totalPages={totalPages}
                onPreviousPage={() => setResultsPage((page) => Math.max(1, page - 1))}
                onNextPage={() => setResultsPage((page) => Math.min(totalPages, page + 1))}
                disablePrevious={resultsPage <= 1 || resultsLoading}
                disableNext={resultsPage >= totalPages || resultsLoading}
              />
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
}
